/**
 * Layer 3 — the weather on the ground, territory wash and fog of war, one shader over the
 * neatline area, drawn above the static features so fog also greys out roads, rivers and glyphs.
 *
 * Inputs: a per-hex RGBA8 texture (R owner, G visible, B land, A weather condition bits)
 * re-uploaded when the view changes, plus the baked coast field (shoreline and slopes).
 *  - weather (docs/Weather.md): wet ground darkens, mud darkens further and holds puddles that
 *    catch the sky, snow lies white with cool shadows on the slopes, frost greys the country,
 *    frozen lakes ice over, morning haze drifts over fogged low ground — all blended between
 *    hexes like the fog of war, so conditions have soft, natural edges
 *  - territory: faction colour at ~20 % inside, rising to ~36 % in a ribbon along its borders
 *    (hex-exact, found by testing the six neighbours in-shader)
 *  - fog: visibility interpolated barycentrically between hex centres (soft ~½-hex edge,
 *    noise-broken), then a graphite veil (desaturate + darken) and pencil hatching whose
 *    spacing stays ~7 css px at any zoom (two octaves cross-faded)
 *  - neutral countries of real-world theatres (closed land, docs/Theatres.md): a grey wash with
 *    counter-diagonal hatching and a thin grey line between neighbouring neutral countries.
 *    The B channel carries it: 255 = belligerent land, 128 + k = neutral country k, 0 = water.
 */
import type { HexGrid } from '@friction/engine';
import { MAX_FACTIONS, NO_FACTION } from '@friction/engine';
import { type Geometry, Mesh, type Shader, type Texture } from 'pixi.js';
import { destroyMesh, FRAG_HEADER, makeDataTexture, makeShader } from '../gfx/gl';
import { rgb01 } from '../palette';
import { GLSL_COAST, GLSL_COMMON } from '../shaders/common';
import { WORLD_QUAD_VERT } from '../shaders/terrain';
import type { SheetLayout } from '../sheet';
import { type FieldTextures, quadGeometry } from './terrain';

const FRAG = /* glsl */ `${FRAG_HEADER}
in vec2 vWorld;
uniform sampler2D uHex;
uniform sampler2D uFieldA;
uniform sampler2D uNoise;
uniform vec4 uBake;
uniform vec2 uBakeSize;
uniform vec2 uGrid;
uniform vec3 uFaction[8];
uniform float uZoom;
uniform float uZoomCss;
uniform float uFog;
uniform float uTint;
uniform float uTime;
uniform float uWeather;
uniform vec2 uWind;

${GLSL_COMMON}
${GLSL_COAST}

const ivec2 DIRS[6] = ivec2[6](ivec2(1, 0), ivec2(1, -1), ivec2(0, -1), ivec2(-1, 0), ivec2(-1, 1), ivec2(0, 1));

vec4 hexData(ivec2 ax) {
  ivec2 o = axialToOffset(ax);
  if (o.x < 0 || o.y < 0 || o.x >= int(uGrid.x) || o.y >= int(uGrid.y)) {
    o = clamp(o, ivec2(0), ivec2(uGrid) - 1);
    vec4 d = texelFetch(uHex, o, 0);
    return vec4(1.0, d.g, 0.0, 0.0);
  }
  return texelFetch(uHex, o, 0);
}

// Mud: short umber dashes on staggered rows — the engraver's sign for soaked ground; the
// symbols thin out towards the edge of the mud (amount < 1).
float mudMarks(vec2 p, float px, float amount) {
  const vec2 CELL = vec2(13.0, 7.0);
  float row = floor(p.y / CELL.y);
  float shift = mod(row, 2.0) * 0.5 * CELL.x;
  vec2 c = vec2(floor((p.x + shift) / CELL.x), row);
  vec2 h = hash22(c + 41.0);
  if (h.y > amount) return 0.0;
  vec2 centre = vec2(c.x * CELL.x - shift, c.y * CELL.y) + CELL * 0.5 + (h - 0.5) * vec2(5.0, 2.0);
  vec2 d = p - centre;
  float dist = length(vec2(max(abs(d.x) - (2.0 + h.x * 2.6), 0.0), d.y));
  return clamp(0.5 + 0.32 / px - dist / px, 0.0, 1.0);
}

float hatchLines(float u, float spacing, float pxWorld) {
  float d = abs(fract(u / spacing + 0.5) - 0.5) * spacing / pxWorld;
  return clamp(0.45 + 0.5 - d, 0.0, 1.0);
}

void main() {
  vec2 p = vWorld;
  float px = 1.0 / uZoom;
  vec2 uv = (p - uBake.xy) * uBake.zw;
  vec4 field = textureBicubic(uFieldA, uv, uBakeSize);
  float coast = field.y + coastWobble(p);
  float cw = max(fwidth(coast), 1e-3);
  float land = smoothstep(-0.7 * cw, 0.7 * cw, coast);

  vec2 a = worldToAxial(p);
  ivec2 hx = cubeRound(a);
  vec4 me = hexData(hx);
  // The organic shoreline puts slivers of land inside water hexes: colour them like the
  // nearest land hex so coasts and lake shores carry no untinted hexagonal halo.
  if (me.b < 0.5 && land > 0.0) {
    float best = 1e9;
    ivec2 pick = hx;
    for (int d = 0; d < 6; d++) {
      ivec2 n = hx + DIRS[d];
      vec4 nd = hexData(n);
      float dd = distance(p, axialToWorld(vec2(n)));
      if (nd.b > 0.5 && dd < best) {
        best = dd;
        pick = n;
      }
    }
    hx = pick;
    me = hexData(hx);
  }
  int own = int(me.r * 255.0 + 0.5);
  vec4 outc = vec4(0.0);

  // Soft per-hex fields: a normalised Gaussian blend (sigma ≈ 0.4 hex) of the hex under p and
  // its six neighbours — visibility for the fog of war, condition bits for the weather.
  ivec2 hc = cubeRound(a);
  float sv = 0.0;
  float sw = 0.0;
  float wet = 0.0;
  float mud = 0.0;
  float snow = 0.0;
  float frost = 0.0;
  float haze = 0.0;
  if (uFog > 0.0 || uWeather > 0.0) {
    for (int k = 0; k < 7; k++) {
      ivec2 n = k == 0 ? hc : hc + DIRS[k - 1];
      vec2 dc = p - axialToWorld(vec2(n));
      float w = exp(-dot(dc, dc) * (1.0 / (2.0 * 25.0 * 25.0)));
      vec4 hd = hexData(n);
      int bits = int(hd.a * 255.0 + 0.5);
      sv += w * hd.g;
      wet += (bits & 128) != 0 ? w : 0.0;
      mud += (bits & 16) != 0 ? w : 0.0;
      frost += (bits & 64) != 0 ? w : 0.0;
      snow += (bits & 32) != 0 ? w : 0.0;
      haze += (bits & 8) != 0 ? w : 0.0;
      sw += w;
    }
    float inv = 1.0 / sw;
    wet *= inv;
    mud *= inv;
    frost *= inv;
    snow *= inv;
    haze *= inv;
  }

  if (uWeather > 0.0) {
    vec4 nz = texture(uNoise, p * (1.0 / 97.0) + vec2(0.23, 0.77));
    // wet ground, then mud: darker umber earth with a broken, organic edge
    float mk = smoothstep(0.25, 0.75, mud + (nz.b - 0.5) * 0.4) * land;
    float wa = land * 0.08 * smoothstep(0.2, 0.7, wet + (nz.g - 0.5) * 0.35) + 0.12 * mk;
    outc = vec4(vec3(0.27, 0.19, 0.11) * wa, wa);
    float detail = smoothstep(0.6, 1.3, uZoomCss);
    if (mk > 0.01 && detail > 0.0) {
      float ma = mudMarks(p, px, mk) * mk * detail * 0.24;
      outc = vec4(vec3(0.3, 0.21, 0.12) * ma, ma) + outc * (1.0 - ma);
      // puddles in the ruts catch the sky, rimmed in dark silt
      float pn = texture(uNoise, p * (1.0 / 21.0) + vec2(0.61, 0.07)).a
               + 0.5 * (texture(uNoise, p * (1.0 / 61.0) + vec2(0.2, 0.9)).g - 0.5);
      float pw = max(fwidth(pn), 1e-3);
      float edge = 0.88 - 0.06 * mk;
      float inside = smoothstep(edge - pw, edge + pw, pn);
      float rim = inside * (1.0 - smoothstep(edge + pw, edge + 3.5 * pw, pn));
      float pa = inside * mk * detail * 0.6;
      outc = vec4(vec3(0.64, 0.71, 0.76) * pa, pa) + outc * (1.0 - pa);
      float ra = rim * mk * detail * 0.45;
      outc = vec4(vec3(0.25, 0.2, 0.15) * ra, ra) + outc * (1.0 - ra);
    }
    // frost greys the country a touch
    float fa = frost * land * 0.07;
    outc = vec4(vec3(0.56, 0.63, 0.7) * fa, fa) + outc * (1.0 - fa);
    // snow: white on the lit slopes, blue-grey in the shadows, broken where it thins out
    vec3 nrm = normalize(vec3(-field.zw * 2.6, 1.0));
    vec3 sun = normalize(vec3(-0.55, -0.75, 0.9));
    float shade = dot(nrm, sun) - sun.z;
    float sk = smoothstep(0.3, 0.7, snow + (nz.a - 0.5) * 0.45) * land;
    vec3 snowCol = mix(vec3(0.975, 0.972, 0.955), vec3(0.72, 0.78, 0.87), clamp(-shade * 1.6, 0.0, 1.0));
    float sa = sk * (0.55 + 0.1 * clamp(shade, 0.0, 1.0));
    outc = vec4(snowCol * sa, sa) + outc * (1.0 - sa);
    // frozen lakes: pale ice with hairline cracks
    int mb = int(me.a * 255.0 + 0.5);
    if (land < 1.0 && (mb & 64) != 0) {
      vec4 cn = texture(uNoise, p * (1.0 / 41.0) + vec2(0.9, 0.3));
      float crack = 1.0 - smoothstep(0.0, 0.02 + 0.6 * px / 41.0, abs(cn.g - 0.5));
      float ia = (1.0 - land) * 0.72;
      vec3 ice = mix(vec3(0.84, 0.89, 0.91), vec3(0.45, 0.55, 0.62), crack * 0.6);
      outc = vec4(ice * ia, ia) + outc * (1.0 - ia);
    }
  }

  int landCode = int(me.b * 255.0 + 0.5);
  if (landCode >= 128 && landCode < 255 && land > 0.0) {
    float na = land * 0.3;
    outc = vec4(vec3(0.47, 0.46, 0.43) * na, na) + outc * (1.0 - na);
    float target = 9.0 / uZoomCss;
    float nh = hatchLines((p.x - p.y) * 0.70710678, target, px);
    float ha = nh * land * 0.16;
    outc = vec4(vec3(0.3, 0.3, 0.3) * ha, ha) + outc * (1.0 - ha);
    // a thin grey rule between two neutral countries
    vec2 c = axialToWorld(vec2(hx));
    for (int d = 0; d < 6; d++) {
      ivec2 n = hx + DIRS[d];
      int nc = int(hexData(n).b * 255.0 + 0.5);
      if (nc >= 128 && nc < 255 && nc != landCode) {
        vec2 cn = axialToWorld(vec2(n));
        float dist = abs(dot((c + cn) * 0.5 - p, normalize(cn - c)));
        float la = (1.0 - smoothstep(0.6 * px, 1.6 * px, dist)) * land * 0.55;
        outc = vec4(vec3(0.33, 0.32, 0.3) * la, la) + outc * (1.0 - la);
      }
    }
  }

  if (own < 8 && uTint > 0.0) {
    vec2 c = axialToWorld(vec2(hx));
    float bandW = max(10.0, 6.0 / uZoomCss);
    float band = 0.0;
    for (int d = 0; d < 6; d++) {
      ivec2 n = hx + DIRS[d];
      vec4 nd = hexData(n);
      if (nd.b > 0.5 && int(nd.r * 255.0 + 0.5) != own) {
        vec2 cn = axialToWorld(vec2(n));
        vec2 dir = normalize(cn - c);
        float dist = dot((c + cn) * 0.5 - p, dir);
        band = max(band, 1.0 - smoothstep(0.0, bandW, dist));
      }
    }
    float ta = land * uTint * (1.0 + 0.8 * band * band);
    outc = vec4(uFaction[own] * ta, ta) + outc * (1.0 - ta);
  }

  if (uFog > 0.0) {
    // Observed areas become soft, rounded pools instead of hexagons: the 0.5 level of the
    // blended visibility sits on the hex edges between observed and unobserved hexes, and the
    // smoothstep feathers the edge over ~half a hex. Low-frequency noise keeps it hand-made.
    float v = sv / sw;
    vec4 nn = texture(uNoise, p * (1.0 / 211.0) + vec2(0.13, 0.61));
    v += (nn.g - 0.5) * 0.16;
    float fog = (1.0 - smoothstep(0.18, 0.78, v)) * uFog;
    if (fog > 0.0) {
      // graphite wash: pulls colours towards a warm grey (desaturate) and down (~-30 %)
      float veilA = fog * mix(0.2, 0.36, land);
      outc = vec4(vec3(0.33, 0.32, 0.30) * veilA, veilA) + outc * (1.0 - veilA);
      // pencil hatching: world-anchored 45° strokes, spacing kept near 6 css px
      float target = 6.0 / uZoomCss;
      float lvl = log2(target / 3.0);
      float l0 = floor(lvl);
      float s0 = 3.0 * exp2(l0);
      float u = (p.x + p.y) * 0.70710678;
      float h = mix(hatchLines(u, s0, px), hatchLines(u, s0 * 2.0, px), smoothstep(0.0, 1.0, lvl - l0));
      vec4 grain = texture(uNoise, p * vec2(1.0 / 11.0, 1.0 / 41.0) + vec2(0.4, 0.2));
      float pencil = 0.45 + 0.55 * smoothstep(0.2, 0.8, grain.a);
      float ha = h * fog * mix(0.05, 0.2, land) * pencil;
      outc = vec4(vec3(0.24, 0.24, 0.25) * ha, ha) + outc * (1.0 - ha);
    }
  }

  // morning haze over fogged ground, drifting slowly with the wind
  if (uWeather > 0.0 && haze > 0.01) {
    vec2 drift = uWind * uTime;
    float h1 = texture(uNoise, (p - drift) * (1.0 / 260.0) + vec2(0.1, 0.4)).g;
    float h2 = texture(uNoise, (p - drift * 1.6) * (1.0 / 83.0) + vec2(0.5, 0.2)).b;
    float hz = smoothstep(0.1, 0.8, haze + (h1 - 0.5) * 0.35) * (0.5 + 0.3 * h1 + 0.2 * h2) * 0.66;
    outc = vec4(vec3(0.93, 0.925, 0.9) * hz, hz) + outc * (1.0 - hz);
  }
  finalColor = outc;
}
`;

export class OverlayLayer {
  readonly mesh: Mesh<Geometry, Shader>;
  private readonly shader: Shader;
  private readonly hexData: Uint8Array;
  private readonly hexTex: Texture;
  private readonly land: Uint8Array;

  /** `neutral(h)`: the neutral country (0..126) of closed land, or -1 for belligerent land and water. */
  constructor(
    grid: HexGrid,
    isLand: (h: number) => boolean,
    sheet: SheetLayout,
    fields: FieldTextures,
    noise: Texture,
    neutral: (h: number) => number = () => -1,
  ) {
    this.hexData = new Uint8Array(grid.count * 4);
    this.land = new Uint8Array(grid.count);
    for (let h = 0; h < grid.count; h++) {
      const k = isLand(h) ? neutral(h) : -1;
      this.land[h] = !isLand(h) ? 0 : k >= 0 ? 128 + (k % 127) : 255;
      this.hexData[h * 4] = NO_FACTION;
      this.hexData[h * 4 + 1] = 255;
      this.hexData[h * 4 + 2] = this.land[h];
    }
    this.hexTex = makeDataTexture({ width: grid.width, height: grid.height, data: this.hexData });
    this.shader = makeShader('friction-overlay', WORLD_QUAD_VERT, FRAG, {
      uHex: this.hexTex.source,
      uFieldA: fields.a.source,
      uNoise: noise.source,
      overlayUniforms: {
        uBake: { value: fields.bake, type: 'vec4<f32>' },
        uBakeSize: { value: fields.bakeSize, type: 'vec2<f32>' },
        uGrid: { value: new Float32Array([grid.width, grid.height]), type: 'vec2<f32>' },
        uFaction: { value: new Float32Array(8 * 3), type: 'vec3<f32>', size: 8 },
        uZoom: { value: 1, type: 'f32' },
        uZoomCss: { value: 1, type: 'f32' },
        uFog: { value: 1, type: 'f32' },
        uTint: { value: 0.2, type: 'f32' },
        uTime: { value: 0, type: 'f32' },
        uWeather: { value: 0, type: 'f32' },
        uWind: { value: new Float32Array([0, 0]), type: 'vec2<f32>' },
      },
    });
    this.mesh = new Mesh({ geometry: quadGeometry(sheet.neat), shader: this.shader });
  }

  private get uniforms() {
    return this.shader.resources.overlayUniforms.uniforms;
  }

  setFactionColors(colors: ReadonlyMap<number, number>): void {
    const arr = this.uniforms.uFaction as Float32Array;
    for (let i = 0; i < Math.min(8, MAX_FACTIONS); i++) {
      const c = colors.get(i);
      if (c !== undefined) rgb01(c, arr, i * 3);
    }
  }

  /**
   * Upload ownership, visibility (null disables fog) and the weather's condition bits (null
   * disables weather; the renderer adds the frozen bit to lakes, see weather.ts).
   */
  setHexes(owner: Uint8Array, visible: Uint8Array | null, conditions: Uint8Array | null = null): void {
    const d = this.hexData;
    for (let h = 0; h < owner.length; h++) {
      d[h * 4] = this.land[h] ? owner[h] : NO_FACTION;
      d[h * 4 + 1] = visible ? (visible[h] ? 255 : 0) : 255;
      d[h * 4 + 3] = conditions ? conditions[h] : 0;
    }
    this.uniforms.uFog = visible ? 1 : 0;
    this.uniforms.uWeather = conditions ? 1 : 0;
    this.hexTex.source.update();
  }

  /** Haze drift in world units per second (0 = still, for reduced motion). */
  setWind(x: number, y: number): void {
    const w = this.uniforms.uWind as Float32Array;
    w[0] = x;
    w[1] = y;
  }

  setTint(strength: number): void {
    this.uniforms.uTint = strength;
  }

  update(zoomCss: number, resolution: number, time: number): void {
    const u = this.uniforms;
    u.uZoom = zoomCss * resolution;
    u.uZoomCss = zoomCss;
    u.uTime = time;
  }

  destroy(): void {
    destroyMesh(this.mesh);
    this.hexTex.destroy(true);
  }
}
